<script setup lang="ts">
import BaseMultiSelect, { type Option } from '@/components/base/BaseMultiSelect.vue'
import { useDebounceFn } from '@vueuse/core'
import CampaignTargetFieldset from '../components/CampaignTargetFieldset.vue'
import LitepieDatepicker from 'litepie-datepicker-tw3'
import { filterStoresByScope, resolveAudienceScopedStoreIds, resolveIncludedAudiences } from '@/utils/audienceStoreIds'
import useAudienceStoreScope from '@/composables/useAudienceStoreScope'

import type { Audience, CampaignChoices, CampaignSelection, CustomerTag, Survey } from '@/types/types'
import type { storeInterface } from '@/types/store'
import type { Contest } from '@/views/contests/Components/types/global'
const props = defineProps<{
  modelValue: CampaignSelection
  stores: storeInterface[]
  genders: string[]
  choices: CampaignChoices
  rewardsValues: string[] | number[]
  contestGames: Contest[]
  surveys: Survey[]
  merchantId: number
  marketingTags: CustomerTag[]
  channelTags: CustomerTag[]
  tags: CustomerTag[]
  lists: Audience[]
}>()

const campaignName = defineModel<string>('campaignName')

const { t } = useI18n()
const { hasModuleBySlug } = useAccess()
const litepieLastVisit = ref(null)
const litepieFirstCheckin = ref(null)

const helpUrl =
  'https://heypongohelp.zendesk.com/hc/fr/articles/22949316359569-Comment-assigner-une-liste-%C3%A0-vos-clients'

const emit = defineEmits<{
  (e: 'update:modelValue', value: CampaignSelection): void
  (e: 'loadMoreAudiences', text: string): void
  (e: 'searchAudienceName', text: string): void
}>()

const hasProcessingSegments = computed(() => {
  return props.lists
    .filter((list) => (props.modelValue.lists as number[]).includes(list.id))
    .some((list) => list.is_processing === true)
})

const selectedItemsLabel = computed(() => {
  return props.modelValue.stores.length
    ? t('global.multiselect.stores.storesSelected', {
        count: props.modelValue.stores.length
      })
    : t('pages.campaign.campaignTargetBlock.select.store.mandatory')
})
const selectedStoreIds = computed(() =>
  (props.modelValue.stores ?? []).map((s) => (s as unknown as Option).value as number)
)

const includedAudiences = computed(() => resolveIncludedAudiences(props.lists, props.modelValue.lists as number[]))
const scopedStoreIds = computed(() => resolveAudienceScopedStoreIds(includedAudiences.value))
const availableStores = computed(() =>
  filterStoresByScope(props.stores, scopedStoreIds.value, (store) => (store as unknown as Option).value as number)
)

const updateStoreIds = (ids: (string | number)[]) => {
  const newStores = props.stores.filter((store) => ids.includes((store as unknown as Option).value))
  emit('update:modelValue', { ...props.modelValue, stores: newStores })
}

useAudienceStoreScope({
  scopedStoreIds,
  getSelectedIds: () => selectedStoreIds.value,
  setSelectedIds: updateStoreIds,
  getRestoreIds: () => props.stores.map((store) => (store as unknown as Option).value as number)
})

const removeStoreId = (deleteStoreId: number) => {
  const newIds = selectedStoreIds.value.filter((id) => id !== deleteStoreId)
  updateStoreIds(newIds)
}

const updateModelValue = (key: keyof CampaignSelection, value: any) => {
  emit('update:modelValue', {
    ...props.modelValue,
    [key]: value
  })
}

const debouncedUpdateAges = useDebounceFn((ages: any) => updateModelValue('ages', ages), 300)
const debouncedUpdateCheckins = useDebounceFn((checkins: any) => updateModelValue('checkins', checkins), 300)
const debouncedUpdatePoints = useDebounceFn((points: any) => updateModelValue('points', points), 300)
</script>

<template>
  <div class="flex flex-col gap-6">
    <section>
      <div class="rounded-md border bg-white">
        <div class="flex flex-col gap-4 p-6">
          <fieldset class="flex flex-col gap-2">
            <AppFormInput
              id="name"
              v-model="campaignName"
              required
              :label="t('pages.campaign.campaignTargetBlock.name')"
            />
          </fieldset>
        </div>
      </div>
    </section>
    <section>
      <div class="rounded-md border bg-white">
        <div class="w-full border-b p-6 text-base font-semibold sm:text-lg">
          {{ t('pages.campaign.campaignTargetBlock.categories.segments') }}
        </div>
        <div class="flex flex-col gap-6 p-6">
          <fieldset class="flex flex-col gap-2">
            <label class="text-sm text-foreground" for="audience">
              {{ t('pages.campaign.campaignTargetBlock.segments.sendTo') }}
            </label>
            <MultiSelectAudiences
              :model-value="modelValue.lists"
              :audiences="lists"
              :show-search-bar="true"
              :default-text="t('pages.campaign.campaignTargetBlock.segments.allCustomers.sms')"
              @load-more="emit('loadMoreAudiences', $event)"
              @search="emit('searchAudienceName', $event)"
              @update:model-value="updateModelValue('lists', $event)"
            />
            <a class="text-xs text-foreground-secondary" :href="helpUrl" target="_blank">
              {{ t('pages.campaign.campaignTargetBlock.segments.moreAbout') }}
              <span class="text-primary underline">
                {{ t('pages.campaign.campaignTargetBlock.audience', 2) }}
              </span>
            </a>

            <BaseTip
              v-if="hasProcessingSegments"
              class="mt-2"
              :text="t('pages.campaign.campaignTargetBlock.segments.processingSegments')"
              severity="info"
            />
          </fieldset>

          <fieldset class="flex flex-col gap-2">
            <label class="text-sm text-foreground">
              {{ t('pages.campaign.campaignTargetBlock.select.store.label', { count: availableStores.length }) }}
            </label>
            <BaseMultiSelect
              :model-value="selectedStoreIds"
              :options="availableStores"
              filter
              display="chip"
              :max-selected-labels="4"
              :selected-items-label="selectedItemsLabel"
              :filter-placeholder="t('global.multiselect.stores.searchPlaceholder')"
              :placeholder="t('global.multiselect.stores.placeholder')"
              @update:model-value="updateStoreIds"
            >
              <template #chip="{ value, option }">
                <BaseChip :text="option.label" removable @remove="removeStoreId(value as number)" />
              </template>
            </BaseMultiSelect>
          </fieldset>

          <fieldset class="flex flex-col gap-2">
            <label class="text-sm text-foreground" for="audience">
              {{ t('pages.campaign.campaignTargetBlock.segments.noSendTo') }}
            </label>
            <MultiSelectAudiences
              :model-value="modelValue.excludedLists"
              :audiences="lists"
              :show-search-bar="true"
              :default-text="t('pages.campaign.campaignTargetBlock.segments.excludeSegments')"
              @load-more="emit('loadMoreAudiences', $event)"
              @search="emit('searchAudienceName', $event)"
              @update:model-value="updateModelValue('excludedLists', $event)"
            />
          </fieldset>
        </div>
      </div>
    </section>

    <section class="rounded-md border bg-white">
      <h4 class="inline-flex w-full items-center border-b p-6 text-sm text-foreground">
        <BaseIcon name="HeroFunnelIconOutline" class="mr-4 h-6 w-6" />
        {{ t('pages.campaign.campaignTargetBlock.refineAudience') }}
      </h4>
      <div class="space-y-6 p-4 sm:p-6">
        <div class="grid grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-2">
          <h3 class="col-span-full text-base font-semibold sm:text-lg">
            {{ t('pages.campaign.campaignCreatePage.segments.profile') }}
          </h3>
          <div class="rounded-md border p-4 sm:p-5">
            <fieldset class="h-full space-y-2">
              <legend class="text-md font-medium text-foreground">
                {{ t('pages.campaign.campaignCreatePage.cards.genders') }}
              </legend>
              <div
                class="flex h-full flex-wrap items-center justify-between gap-3 sm:grid sm:grid-cols-2 lg:grid-cols-3"
              >
                <div v-for="gender in genders" :key="gender" class="col-span-1">
                  <AppCheckboxField
                    :id="`gender[${gender}]`"
                    :model-value="modelValue.genders"
                    :value="gender"
                    :label="t(`global.gender.${gender}`)"
                    @update:model-value="updateModelValue('genders', $event)"
                  />
                </div>
              </div>
            </fieldset>
          </div>
          <div class="rounded-md border p-4 sm:p-5">
            <fieldset class="space-y-2">
              <legend class="text-md font-medium text-foreground">
                {{ t('pages.campaign.campaignCreatePage.cards.ages') }}
              </legend>
              <div class="flex flex-wrap gap-3 sm:grid sm:grid-cols-2">
                <div class="flex w-full min-w-fit flex-col">
                  <label for="minAge" class="mb-1.5 text-sm font-normal leading-6 text-foreground">{{
                    t('pages.campaign.campaignTargetBlock.select.from')
                  }}</label>
                  <AppFormInput
                    id="minAge"
                    :modelValue="modelValue.ages[0]"
                    type="number"
                    @update:model-value="debouncedUpdateAges([$event, modelValue.ages[1]])"
                  />
                </div>
                <div class="flex w-full min-w-fit flex-col">
                  <label for="maxAge" class="mb-1.5 text-sm font-normal leading-6 text-foreground">{{
                    t('pages.campaign.campaignTargetBlock.select.to')
                  }}</label>
                  <AppFormInput
                    id="maxAge"
                    :modelValue="modelValue.ages[1]"
                    type="number"
                    @update:model-value="debouncedUpdateAges([modelValue.ages[0], $event])"
                  />
                </div>
              </div>
            </fieldset>
          </div>
        </div>
        <CampaignTargetFieldset
          :fieldset-main-name="t('pages.campaign.campaignCreatePage.segments.channel')"
          :fieldset-legends="[t('pages.campaign.campaignCreatePage.cards.channelTags')]"
        >
          <template #1>
            <div class="flex flex-wrap gap-5 py-1.5">
              <AppCheckboxField
                v-for="tag in channelTags"
                :id="`tag[${tag.id}]`"
                :key="tag.id"
                :model-value="modelValue.tags"
                :value="tag.id"
                @update:model-value="updateModelValue('tags', $event)"
              >
                <template #label>
                  <BaseChip :color="tag.color" :text="tag.name" />
                </template>
              </AppCheckboxField>
            </div>
          </template>
        </CampaignTargetFieldset>
        <CampaignTargetFieldset
          :fieldset-main-name="t('pages.campaign.campaignDetailPage.cards.checkinConvertedCustomersNb')"
          :fieldset-legends="[
            t('pages.campaign.campaignCreatePage.cards.date.dates'),
            t('pages.campaign.campaignCreatePage.cards.checkinCount')
          ]"
        >
          <template #1>
            <div class="grid items-start justify-between gap-3 sm:grid-cols-2">
              <div class="space-y-2">
                <label class="text-sm font-normal text-foreground">
                  {{ t('pages.campaign.campaignCreatePage.cards.date.lastVisit') }}
                </label>
                <LitepieDatepicker
                  ref="litepieLastVisit"
                  :model-value="modelValue.lastVisit"
                  as-single
                  use-range
                  i18n="fr"
                  :formatter="{
                    date: 'DD/MM/YYYY',
                    month: 'MMM'
                  }"
                  @update:model-value="updateModelValue('lastVisit', $event)"
                  @click="litepieLastVisit.isShow = !litepieLastVisit.isShow"
                />
              </div>
              <div class="space-y-2">
                <label class="mb-1.5 text-sm font-normal text-foreground">
                  {{ t('pages.campaign.campaignCreatePage.cards.date.firstCheckin') }}
                </label>
                <LitepieDatepicker
                  ref="litepieFirstCheckin"
                  :model-value="modelValue.firstCheckin"
                  as-single
                  use-range
                  i18n="fr"
                  :formatter="{
                    date: 'DD/MM/YYYY',
                    month: 'MMM'
                  }"
                  @update:model-value="updateModelValue('firstCheckin', $event)"
                  @click="litepieFirstCheckin.isShow = !litepieFirstCheckin.isShow"
                />
              </div>
            </div>
          </template>
          <template #2>
            <div class="grid gap-3 sm:grid-cols-2">
              <div class="space-y-2">
                <label for="minVisits" class="text-sm font-normal text-foreground">{{
                  t('pages.campaign.campaignTargetBlock.select.between')
                }}</label>
                <AppFormInput
                  id="minVisits"
                  :model-value="modelValue.checkins[0]"
                  class="text-foreground-secondary"
                  type="number"
                  :min="0"
                  :max="5000"
                  @update:model-value="debouncedUpdateCheckins([$event, modelValue.checkins[1]])"
                  @wheel.prevent
                >
                  <template #icon-right>
                    <span class="text-sm text-muted-foreground">
                      {{ t('pages.campaign.campaignDetailPage.cards.checkinConvertedCustomersNb').toLowerCase() }}
                    </span>
                  </template>
                </AppFormInput>
              </div>
              <div class="space-y-2">
                <label for="maxVisits" class="text-sm font-normal text-foreground">{{
                  t('pages.campaign.campaignTargetBlock.select.and')
                }}</label>
                <AppFormInput
                  id="maxVisits"
                  :model-value="modelValue.checkins[1]"
                  class="text-foreground-secondary"
                  type="number"
                  :min="0"
                  :max="5000"
                  @update:model-value="debouncedUpdateCheckins([modelValue.checkins[0], $event])"
                  @wheel.prevent
                >
                  <template #icon-right>
                    <span class="text-sm text-muted-foreground">
                      {{ t('pages.campaign.campaignDetailPage.cards.checkinConvertedCustomersNb').toLowerCase() }}
                    </span>
                  </template>
                </AppFormInput>
              </div>
            </div>
          </template>
        </CampaignTargetFieldset>
        <CampaignTargetFieldset
          :fieldset-main-name="t('pages.campaign.campaignCreatePage.segments.engagement')"
          :fieldset-legends="[t('pages.campaign.campaignCreatePage.cards.marketingTags')]"
        >
          <template #1>
            <div class="grid gap-5 py-1.5 sm:grid-cols-2 xl:grid-cols-3">
              <AppCheckboxField
                v-for="tag in marketingTags"
                :id="`tag[${tag.id}]`"
                :key="tag.id"
                :model-value="modelValue.tags"
                :value="tag.id"
                @update:model-value="updateModelValue('tags', $event)"
              >
                <template #label>
                  <BaseChip :color="tag.color" :text="tag.name" />
                </template>
              </AppCheckboxField>
            </div>
          </template>
        </CampaignTargetFieldset>
        <CampaignTargetFieldset :fieldset-legends="[t('pages.campaign.campaignCreatePage.cards.points')]">
          <template #1>
            <div class="grid gap-3 sm:grid-cols-2">
              <div>
                <label class="text-sm font-normal leading-6 text-foreground">{{
                  t('pages.campaign.campaignTargetBlock.select.between')
                }}</label>
                <AppFormInput
                  id="minPoints"
                  :model-value="modelValue.points[0]"
                  class="mt-1.5 text-foreground-secondary"
                  type="number"
                  :min="0"
                  :max="5000"
                  @update:model-value="debouncedUpdatePoints([$event, modelValue.points[1]])"
                  @wheel.prevent
                >
                  <template #icon-right>
                    <span class="text-sm text-muted-foreground">
                      {{ t('pages.customer.customerDetailPage.blocks.points.points') }}
                    </span>
                  </template>
                </AppFormInput>
              </div>
              <div>
                <label class="text-sm font-normal leading-6 text-foreground">{{
                  t('pages.campaign.campaignTargetBlock.select.and')
                }}</label>
                <AppFormInput
                  id="maxPoints"
                  :model-value="modelValue.points[1]"
                  class="mt-1.5 text-foreground-secondary"
                  type="number"
                  :min="0"
                  :max="5000"
                  @update:model-value="debouncedUpdatePoints([modelValue.points[0], $event])"
                  @wheel.prevent
                >
                  <template #icon-right>
                    <span class="text-sm text-muted-foreground">
                      {{ t('pages.customer.customerDetailPage.blocks.points.points') }}
                    </span>
                  </template>
                </AppFormInput>
              </div>
            </div>
          </template>
        </CampaignTargetFieldset>
        <CampaignTargetFieldset
          v-if="hasModuleBySlug('game-contest') && contestGames.length > 0"
          :fieldset-legends="[t('pages.campaign.campaignCreatePage.cards.contestGames')]"
        >
          <template #1>
            <div class="grid gap-5 py-1.5 font-normal lg:grid-cols-3">
              <AppCheckboxField
                v-for="game in contestGames"
                :id="`game[${game.id}]`"
                :key="game.id"
                :model-value="modelValue.contestGameIds"
                :value="game.id"
                :label="game.name"
                @update:model-value="updateModelValue('contestGameIds', $event)"
              />
              <div v-if="contestGames.length === 0" class="mb-4 text-sm text-foreground">
                {{ t('global.words.noResult') }}
              </div>
            </div>
          </template>
        </CampaignTargetFieldset>
        <CampaignTargetFieldset
          v-if="surveys.length"
          :fieldset-legends="[t('pages.campaign.campaignCreatePage.cards.surveys')]"
        >
          <template #1>
            <div
              v-for="(survey, surveyIndex) in surveys"
              :key="survey.id"
              :class="[surveyIndex < surveys.length - 1 ? 'border-b' : '', 'mb-4 py-4']"
            >
              <div class="mb-4 text-sm text-foreground">
                {{ survey.questionText }}
              </div>
              <div v-for="choice in survey.choices" :key="choice.id" class="mb-2 flex items-start px-4 py-1">
                <AppCheckboxField
                  :id="`choice[${choice.id}]`"
                  :model-value="modelValue.surveyQuestionChoices"
                  :value="choice.id"
                  :label="choice.text"
                  @update:model-value="updateModelValue('surveyQuestionChoices', $event)"
                />
                <span
                  class="ml-2 flex-none rounded-xs px-2 py-0.5 text-xs font-bold"
                  :class="choice.isHighest ? 'bg-foreground text-white' : 'bg-stone-200 text-foreground'"
                >
                  {{ Math.round(choice.answersPercentage) }} %
                </span>
              </div>
            </div>
          </template>
        </CampaignTargetFieldset>
      </div>
    </section>
  </div>
</template>
